import classNames from "classnames";
import { memo } from "react";
import { Spinner } from "react-bootstrap";

import { MutationErrorIcon } from "@/components/MutationErrorIcon";
import { Job } from "@/features/job-tracking/queries/job-by-name";
import { BomNode } from "@/features/job-tracking/queries/tracking-data/bom-hierarchy-generator";
import { useUpdatePurchasingPriorityMutation } from "../job-tracking/queries/tracking-data/useUpdatePurchasingPriorityMutation";
import { BomNodePurchasingPriorityIcon } from "./BomNodePurchasingPriorityIcon";
import { CreateJobSpecificPurchasingPriorityOverridesButton } from "./CreateJobSpecificPurchasingPriorityOverridesButton";
import { DeletePurchasingPriorityButton } from "./DeletePurchasingPriorityButton";
import { PurchasingPriorityButtonGroup } from "./PurchasingPriorityButtonGroup";
import { PurchasingPriorityOverrideFlag } from "./PurchasingPriorityOverrideFlag";
import { RecursiveDeletePurchasingPrioritiesButton } from "./RecursiveDeletePurchasingPrioritiesButton";
import { SavePurchasingPriorityTemplateButton } from "./SavePurchasingPriorityTemplateButton";

/**
 * Renders a cell for viewing and updating the purchasing priority of a BOM node.
 */
const PurchasingPriorityCellInternal: React.FC<{ job: Job; bomNode: BomNode }> = ({ job, bomNode }) => {
  const updatePurchasingPriorityMutation = useUpdatePurchasingPriorityMutation(job);

  const { appliedRecordSource, childOverrides } = bomNode.purchasingPriority;
  return (
    <div
      className={classNames("d-flex align-items-center text-body", {
        "text-opacity-25": appliedRecordSource === "default",
        "text-opacity-50": appliedRecordSource === "template",
      })}
    >
      <pre className="m-0 lh-1" style={{ fontSize: "calc(var(--bs-body-line-height) * 1em)" }}>
        {bomNode.asciiTreePrefix}
      </pre>
      <BomNodePurchasingPriorityIcon {...bomNode} />
      <PurchasingPriorityButtonGroup
        bomNode={bomNode}
        updatePurchasingPriorityMutation={updatePurchasingPriorityMutation}
      />
      <PurchasingPriorityOverrideFlag overriddenChildren={childOverrides.job} sourceType="job" />
      <PurchasingPriorityOverrideFlag overriddenChildren={childOverrides.template} sourceType="template" />
      {bomNode.recursiveDepth === 0 && bomNode.purchasingPriority.appliedRecordSource !== "job" && (
        <CreateJobSpecificPurchasingPriorityOverridesButton
          bomNode={bomNode}
          updatePurchasingPriorityMutation={updatePurchasingPriorityMutation}
          className="p-0 ms-2"
        />
      )}
      {bomNode.recursiveDepth === 0 && bomNode.purchasingPriority.nodeJobRecord && (
        <>
          <SavePurchasingPriorityTemplateButton
            bomNode={bomNode}
            updatePurchasingPriorityMutation={updatePurchasingPriorityMutation}
            className="p-0 ms-2"
          />
          <RecursiveDeletePurchasingPrioritiesButton
            bomNode={bomNode}
            updatePurchasingPriorityMutation={updatePurchasingPriorityMutation}
            className="p-0 ms-1"
          />
        </>
      )}
      {bomNode.recursiveDepth !== 0 && bomNode.purchasingPriority.nodeJobRecord && (
        <DeletePurchasingPriorityButton
          purchasingPriority={bomNode.purchasingPriority}
          updatePurchasingPriorityMutation={updatePurchasingPriorityMutation}
          className="p-0 ms-1"
        />
      )}
      {updatePurchasingPriorityMutation.isPending && <Spinner size="sm" className="ms-1" />}
      <MutationErrorIcon mutation={updatePurchasingPriorityMutation} className="ms-1 lh-1 p-0" />
    </div>
  );
};

export const PurchasingPriorityCell = memo(PurchasingPriorityCellInternal);
